import * as React from 'react';
import moment from 'moment-timezone';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import { dateToMinutes } from 'components/form/TimePicker';
import Checkbox from 'components/form/Checkbox';
import TimePopover from './TimePopover';
import extend from 'lodash/extend';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/DeleteOutlined';
import CopyIcon from '@mui/icons-material/ContentCopyOutlined';
import IconButton from 'components/IconButton';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import Box from '@mui/material/Box';
import { WEEKDAYS } from '../utils';

export function timeStrToMinutes(val) {
  const [hours, minutes] = val.split(':');
  const m = moment
    .utc()
    .startOf('day')
    .add(hours, 'hours')
    .add(minutes, 'minutes');
  return dateToMinutes(m);
}

export const fromDbValue = (value) => {
  if (typeof value === 'string' && value) {
    const [from, to] = value.split('-');
    return {
      from: timeStrToMinutes(from),
      to: timeStrToMinutes(to),
    };
  } else {
    return value;
  }
};

function doesNotOverlapWithUpdate(updatedSlot, existingSlots, index) {
  const existingSlot = existingSlots[index];
  for (const slot of existingSlots) {
    if (existingSlot === slot) continue;
    if (updatedSlot.from >= slot.to || updatedSlot.to <= slot.from) {
      continue; // No overlap, check the next existing slot.
    } else {
      return false; // 'from' value overlaps with an existing slot.
    }
  }
  return true; // 'from' value does not overlap with any existing slots.
}

const WeekdaySchedule = ({ value, onChange, weekday }) => {
  const state = usePopupState({ popupId: weekday, variant: 'popover' });
  const [selectedDays, setSelectedDays] = React.useState([]);

  const onChangeFrom = (idx) => (time) => {
    const val = value[idx];
    if (val.to <= time) {
      throw new Error(
        `"From" time cannot be greater than or same as "To" time`,
      );
    }
    if (value.length) {
      // check overlap with other slots
      const noOverlap = doesNotOverlapWithUpdate(
        { from: time, to: val.to },
        value,
        idx,
      );

      if (!noOverlap) {
        throw new Error(`Time slot overlaps with another slot`);
      }
    }

    extend(val, { from: time });
    onChange({
      [weekday]: value,
    });
  };

  const onChangeTo = (idx) => (time) => {
    const val = value[idx];
    if (val.from >= time) {
      throw new Error(
        `"From" time cannot be greater than or same as "To" time`,
      );
    }
    if (value.length) {
      const noOverlap = doesNotOverlapWithUpdate(
        { from: val.from, to: time },
        value,
        idx,
      );
      if (!noOverlap) {
        throw new Error(`Time slot overlaps with another slot`);
      }
    }

    extend(val, { to: time });
    onChange({
      [weekday]: value,
    });
  };

  const onToggle = (e) => {
    const { checked } = e.target;
    if (checked) {
      onChange({
        [weekday]: [{ from: 480, to: 1020 }],
      });
    } else {
      onChange({
        [weekday]: [],
      });
    }
  };

  const onRemove = (idx) => {
    console.log(value);
    onChange({
      [weekday]: value.filter((v, i) => i !== idx),
    });
  };

  const onAdd = () => {
    if (value.length) {
      const last = value[value.length - 1];
      onChange({
        [weekday]: [
          ...value,
          {
            from: last.to,
            to: last.to + 60,
          },
        ],
      });
    } else {
      onChange({
        [weekday]: [{ from: 480, to: 1020 }],
      });
    }
  };

  const onCopy = () => {
    state.close();
    const values = {};
    selectedDays.forEach((day) => {
      values[day] = value.map((i) => ({
        from: i.from,
        to: i.to,
      }));
    });
    onChange(values);
    setSelectedDays([]);
  };

  return (
    <Stack
      component={Paper}
      variant="outlined"
      sx={{ bgcolor: 'background.default', px: 1 }}
      direction="row"
      spacing={2}
      position="relative"
    >
      <Checkbox
        sx={{ width: 75 }}
        margin="none"
        size="small"
        checked={value?.length > 0}
        onChange={onToggle}
        label={weekday}
      />
      {value === undefined ? null : (
        <Stack justifyContent="center">
          {value.map((i, idx) => (
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="flex-end"
              >
                <TimePopover
                  isDefault={!value}
                  value={i.from}
                  onChange={onChangeFrom(idx)}
                />
                <Typography
                  variant="caption"
                  fontWeight={500}
                  color="text.secondary"
                >
                  TO
                </Typography>
                <TimePopover
                  isDefault={!value}
                  value={i.to}
                  onChange={onChangeTo(idx)}
                />
                <IconButton
                  color="error"
                  onClick={() => onRemove(idx)}
                  size="small"
                  icon={<RemoveIcon fontSize="small" />}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      <Stack
        spacing={1}
        direction="row"
        alignItems="center"
        position="absolute"
        right={0}
        top={5}
      >
        <IconButton
          onClick={onAdd}
          size="small"
          icon={<AddIcon fontSize="small" />}
        />
        <IconButton
          {...bindTrigger(state)}
          size="small"
          edge="end"
          icon={<CopyIcon fontSize="small" />}
        />
      </Stack>
      <Popover {...bindPopover(state)}>
        <Box p={2}>
          <Typography gutterBottom color="text.secondary" variant="body2">
            Copy to..
          </Typography>
          {WEEKDAYS.map((day, index) => (
            <Checkbox
              disabled={weekday === day}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedDays([...selectedDays, day]);
                } else {
                  setSelectedDays(selectedDays.filter((i) => i !== day));
                }
              }}
              checked={selectedDays.includes(day)}
              key={day}
              size="small"
              margin="none"
              label={day}
            />
          ))}
          <Button fullWidth size="small" sx={{ mt: 1 }} onClick={onCopy}>
            Apply
          </Button>
        </Box>
      </Popover>
    </Stack>
  );
};

const Scheduler = React.forwardRef(
  ({ value, onChange, hasError, helpText }, ref) => {
    const [t] = useTranslation('talk');

    const handleChange = (val) => {
      onChange({
        ...value,
        ...val,
      });
    };

    return (
      <FormControl fullWidth margin="dense" error={hasError}>
        <Typography gutterBottom fontWeight={500}>
          {t('set-days-time', 'Set days and time')}
        </Typography>
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderColor: hasError && 'error.main' }}
        >
          <Stack spacing={1}>
            {WEEKDAYS.map((day, index) => {
              const v = value?.[day] || [];

              const initialValue = v.map((i) => {
                return {
                  from: i.from ?? timeStrToMinutes(i.call_hour_group_opens_at),
                  to: i.to ?? timeStrToMinutes(i.call_hour_group_closes_at),
                  ...i,
                };
              });

              return (
                <WeekdaySchedule
                  key={day}
                  weekday={day}
                  onChange={handleChange}
                  value={initialValue}
                />
              );
            })}
          </Stack>
        </Paper>
        {hasError && <FormHelperText sx={{ m: 0 }}>{helpText}</FormHelperText>}
      </FormControl>
    );
  },
);

export default Scheduler;
